/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useCallback } from 'react'
import api from '../../services/axios'
import { toast } from 'react-toastify'
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiRefreshCw,
  FiFolder,
  FiSearch,
  FiFileText,
  FiClock,
  FiX,
  FiLayers,
} from 'react-icons/fi'
import { ThreeDot } from 'react-loading-indicators'
import {
  Container,
  Header,
  StatsGrid,
  StatCard,
  FilterBar,
  TabButton,
  SearchInput,
  SelectBox,
  RefreshButton,
  TableContainer,
  Table,
  StatusBadge,
  TypeBadge,
  ActionButton,
  Pagination,
  ModalBackdrop,
  ModalContent,
} from './styles'

interface FileLog {
  id: number
  tipo: 'ESTOQUE' | 'SELLOUT' | 'PRODUTOS' | 'CLIENTES' | 'FORCAVE'
  nome_arquivo: string
  caminho_arquivo: string
  dist_id: number | null
  dist_nome: string | null
  status: 'SUCESSO' | 'ERRO' | 'REPROCESSADO'
  erro_mensagem: string | null
  total_linhas: number | null
  total_quantidade: number | null
  created_at: string
  reprocessado_em: string | null
}

interface FileStats {
  total: number
  total_erros: number
  total_sucesso: number
  total_reprocessados: number
  total_hoje: number
}

export default function FileLogsView() {
  const [logs, setLogs] = useState<FileLog[]>([])
  const [stats, setStats] = useState<FileStats>({
    total: 0,
    total_erros: 0,
    total_sucesso: 0,
    total_reprocessados: 0,
    total_hoje: 0,
  })
  const [loading, setLoading] = useState(false)
  const [reprocessingId, setReprocessingId] = useState<number | null>(null)

  // Filtros
  const [activeTab, setActiveTab] = useState<'TODOS' | 'ERRO' | 'REPROCESSADO' | 'SUCESSO'>('TODOS')
  const [tipoFilter, setTipoFilter] = useState<'TODOS' | 'ESTOQUE' | 'SELLOUT'>('TODOS')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)

  // Modal de B.O. / Erro
  const [selectedLog, setSelectedLog] = useState<FileLog | null>(null)

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/arquivos/stats')
      setStats(res.data)
    } catch (err) {
      console.error('Erro ao buscar estatísticas:', err)
    }
  }, [])

  const fetchLogs = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/admin/arquivos/logs', {
        params: {
          page,
          limit: 20,
          status: activeTab === 'TODOS' ? '' : activeTab,
          tipo: tipoFilter === 'TODOS' ? '' : tipoFilter,
          search: search || undefined,
        },
      })
      setLogs(res.data.logs || [])
      setTotalPages(res.data.totalPages || 1)
      setTotalRecords(res.data.total || 0)
    } catch (err: any) {
      console.error('Erro ao buscar logs de arquivos:', err)
      toast.error('Erro ao carregar lista de arquivos.')
    } finally {
      setLoading(false)
    }
  }, [page, activeTab, tipoFilter, search])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  const handleReprocess = async (log: FileLog) => {
    setReprocessingId(log.id)
    try {
      const res = await api.post(`/admin/arquivos/reprocessar/${log.id}`)
      toast.success(res.data.message || 'Arquivo reprocessado com sucesso!')
      if (selectedLog && selectedLog.id === log.id) {
        setSelectedLog(null)
      }
      fetchStats()
      fetchLogs()
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.details || 'Falha ao reprocessar arquivo.'
      toast.error(msg)
      fetchLogs()
    } finally {
      setReprocessingId(null)
    }
  }

  const [syncing, setSyncing] = useState(false)

  const handleSincronizar = async () => {
    setSyncing(true)
    try {
      const res = await api.post('/admin/arquivos/sincronizar')
      toast.success(res.data.message || 'Pastas escaneadas com sucesso!')
      await fetchStats()
      await fetchLogs()
    } catch (err: any) {
      toast.error('Erro ao escanear pastas do servidor.')
    } finally {
      setSyncing(false)
    }
  }

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '—'
    const dt = new Date(dateStr)
    if (isNaN(dt.getTime())) return '—'
    return dt.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <Container>
      <Header>
        <div className='title-group'>
          <h2>
            <FiFolder /> Monitor de Ingestão e Reprocessamento
          </h2>
          <p>
            Auditoria completa de arquivos recebidos via FTP/Upload, captura de erros (B.O.) e reprocessamento sob demanda.
          </p>
        </div>

        <div className='actions-group' style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            type='button'
            onClick={handleSincronizar}
            disabled={syncing}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#001D53',
              color: '#a6c10b',
              border: '1px solid #a6c10b',
              borderRadius: '8px',
              padding: '8px 14px',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: syncing ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
            }}
            title='Escanear pastas C:\tmp\logs e C:\tmp\arquivos no servidor'
          >
            <FiRefreshCw className={syncing ? 'spinning' : ''} />
            {syncing ? 'Escaneando Pastas...' : 'Escanear C:\\tmp'}
          </button>
          <RefreshButton
            onClick={() => {
              fetchStats()
              fetchLogs()
            }}
            title='Atualizar registros'
          >
            <FiRefreshCw className={loading ? 'spinning' : ''} />
          </RefreshButton>
        </div>
      </Header>

      {/* Grid de Métricas */}
      <StatsGrid>
        <StatCard $variant='blue'>
          <div className='icon-box'>
            <FiFileText />
          </div>
          <div className='stat-info'>
            <h4>Total Recebido</h4>
            <div className='value'>{stats.total.toLocaleString()}</div>
          </div>
        </StatCard>

        <StatCard $variant='red'>
          <div className='icon-box'>
            <FiAlertTriangle />
          </div>
          <div className='stat-info'>
            <h4>Arquivos com B.O.</h4>
            <div className='value'>{stats.total_erros.toLocaleString()}</div>
          </div>
        </StatCard>

        <StatCard $variant='purple'>
          <div className='icon-box'>
            <FiRefreshCw />
          </div>
          <div className='stat-info'>
            <h4>Reprocessados</h4>
            <div className='value'>{stats.total_reprocessados.toLocaleString()}</div>
          </div>
        </StatCard>

        <StatCard $variant='green'>
          <div className='icon-box'>
            <FiCheckCircle />
          </div>
          <div className='stat-info'>
            <h4>Sucessos</h4>
            <div className='value'>{stats.total_sucesso.toLocaleString()}</div>
          </div>
        </StatCard>
      </StatsGrid>

      {/* Barra de Filtros */}
      <FilterBar>
        <div className='tabs'>
          <TabButton $active={activeTab === 'TODOS'} onClick={() => { setActiveTab('TODOS'); setPage(1) }}>
            Todos <span className='badge-count'>{stats.total}</span>
          </TabButton>
          <TabButton $active={activeTab === 'ERRO'} onClick={() => { setActiveTab('ERRO'); setPage(1) }}>
            Com B.O. (Erros) <span className='badge-count'>{stats.total_erros}</span>
          </TabButton>
          <TabButton $active={activeTab === 'REPROCESSADO'} onClick={() => { setActiveTab('REPROCESSADO'); setPage(1) }}>
            Reprocessados <span className='badge-count'>{stats.total_reprocessados}</span>
          </TabButton>
          <TabButton $active={activeTab === 'SUCESSO'} onClick={() => { setActiveTab('SUCESSO'); setPage(1) }}>
            Sucessos <span className='badge-count'>{stats.total_sucesso}</span>
          </TabButton>
        </div>

        <div className='filter-controls'>
          <SelectBox
            value={tipoFilter}
            onChange={(e) => { setTipoFilter(e.target.value as any); setPage(1) }}
          >
            <option value='TODOS'>Todos os Tipos</option>
            <option value='ESTOQUE'>Estoque</option>
            <option value='SELLOUT'>Sellout</option>
          </SelectBox>

          <SearchInput>
            <FiSearch />
            <input
              type='text'
              placeholder='Buscar arquivo ou distribuidor...'
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            />
          </SearchInput>
        </div>
      </FilterBar>

      {/* Tabela de Arquivos */}
      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Data/Hora</th>
              <th>Tipo</th>
              <th>Distribuidor</th>
              <th>Nome do Arquivo</th>
              <th>Status</th>
              <th>Linhas / Qtd</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '40px' }}>
                  <ThreeDot color='#3b82f6' size='medium' />
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  Nenhum arquivo encontrado para os filtros selecionados.
                </td>
              </tr>
            ) : (
              logs.map((log) => {
                const isReprocessing = reprocessingId === log.id
                return (
                  <tr key={log.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FiClock style={{ color: '#64748b' }} />
                        {formatDateTime(log.created_at)}
                      </div>
                    </td>
                    <td>
                      <TypeBadge $tipo={log.tipo}>{log.tipo}</TypeBadge>
                    </td>
                    <td>
                      <strong>{log.dist_nome || 'Não identificado'}</strong>
                    </td>
                    <td title={log.nome_arquivo} style={{ maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.nome_arquivo}
                    </td>
                    <td>
                      <StatusBadge $status={log.status}>
                        {log.status === 'ERRO' && <FiAlertTriangle size={12} />}
                        {log.status === 'REPROCESSADO' && <FiRefreshCw size={12} />}
                        {log.status === 'SUCESSO' && <FiCheckCircle size={12} />}
                        {log.status}
                      </StatusBadge>
                    </td>
                    <td>
                      {log.total_linhas != null
                        ? `${log.total_linhas.toLocaleString()} lins`
                        : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {log.erro_mensagem && (
                          <ActionButton
                            $variant='danger'
                            onClick={() => setSelectedLog(log)}
                            title='Ver detalhes do erro'
                          >
                            <FiAlertTriangle /> Ver B.O.
                          </ActionButton>
                        )}

                        <ActionButton
                          $variant='primary'
                          disabled={isReprocessing}
                          onClick={() => handleReprocess(log)}
                          title='Reprocessar este arquivo'
                        >
                          <FiRefreshCw className={isReprocessing ? 'spinning' : ''} />
                          {isReprocessing ? 'Processando...' : 'Reprocessar'}
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Pagination>
        <span>
          Mostrando {logs.length} de {totalRecords} arquivos
        </span>
        <div className='page-controls'>
          <ActionButton
            $variant='ghost'
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </ActionButton>
          <span style={{ display: 'flex', alignItems: 'center', padding: '0 8px', fontWeight: 600 }}>
            {page} / {totalPages}
          </span>
          <ActionButton
            $variant='ghost'
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Próxima
          </ActionButton>
        </div>
      </Pagination>

      {/* Modal de Detalhes do Erro (B.O.) */}
      {selectedLog && (
        <ModalBackdrop onClick={() => setSelectedLog(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <div className='modal-header'>
              <h3>
                <FiAlertTriangle /> Detalhes da Falha (B.O.)
              </h3>
              <button onClick={() => setSelectedLog(null)}>
                <FiX />
              </button>
            </div>

            <div className='meta-grid'>
              <div className='item'>
                <span className='label'>Arquivo</span>
                <span className='val'>{selectedLog.nome_arquivo}</span>
              </div>
              <div className='item'>
                <span className='label'>Distribuidor</span>
                <span className='val'>{selectedLog.dist_nome || 'Desconhecido'}</span>
              </div>
              <div className='item'>
                <span className='label'>Data de Recebimento</span>
                <span className='val'>{formatDateTime(selectedLog.created_at)}</span>
              </div>
              <div className='item'>
                <span className='label'>Tipo de Arquivo</span>
                <span className='val'>{selectedLog.tipo}</span>
              </div>
              <div className='item' style={{ gridColumn: 'span 2' }}>
                <span className='label'>Caminho Físico (Servidor)</span>
                <span className='val' style={{ fontSize: '11px', color: '#94a3b8' }}>
                  {selectedLog.caminho_arquivo}
                </span>
              </div>
            </div>

            <div>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#f87171', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                <FiLayers /> Mensagem de Erro Retornada:
              </span>
              <div className='error-block'>
                {selectedLog.erro_mensagem || 'Nenhum detalhe adicional de erro disponível.'}
              </div>
            </div>

            <div className='modal-footer'>
              <ActionButton $variant='ghost' onClick={() => setSelectedLog(null)}>
                Fechar
              </ActionButton>
              <ActionButton
                $variant='primary'
                disabled={reprocessingId === selectedLog.id}
                onClick={() => handleReprocess(selectedLog)}
              >
                <FiRefreshCw className={reprocessingId === selectedLog.id ? 'spinning' : ''} />
                {reprocessingId === selectedLog.id ? 'Reprocessando...' : 'Reprocessar Arquivo Agora'}
              </ActionButton>
            </div>
          </ModalContent>
        </ModalBackdrop>
      )}
    </Container>
  )
}
